<template>
  <div :class="cn('j-section-title', variantClasses, props.class)">
    <div class="j-section-title-content">
      <JIcon v-if="icon" :name="icon" class="j-section-title-icon" />
      <div class="j-section-title-text-wrapper">
        <h3 class="j-section-title-text">{{ title }}</h3>
        <p v-if="subtitle" class="j-section-subtitle-text">{{ subtitle }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { cn } from '@/lib/utils'
import { JIcon } from '@/components/atoms'
import type { JSectionTitleProps, SectionTitleVariant } from '@/types/section-title.types'

const props = withDefaults(defineProps<JSectionTitleProps>(), {
  variant: 'default',
})

const VARIANT_STYLES: Record<SectionTitleVariant, string> = {
  default: 'border-l-border text-foreground dark:border-l-border',
  primary: 'border-l-primary text-primary',
  secondary: 'border-l-secondary text-secondary',
  accent: 'border-l-accent text-accent',
}

const variantClasses = computed(() => {
  return VARIANT_STYLES[props.variant] || VARIANT_STYLES.default
})
</script>

<style scoped>
.j-section-title {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.75rem 1rem;
  border-left: 3px solid;
  border-radius: 0.375rem;
  background: hsl(var(--card));
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition: all 0.2s ease;
  overflow: hidden;
}

.j-section-title::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: currentColor;
  opacity: 0.15;
}

.j-section-title:hover {
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / 0.08);
  transform: translateY(-1px);
}

.j-section-title-content {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
}

.j-section-title-icon {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
  color: currentColor;
}

.j-section-title-text-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.j-section-title-text {
  font-size: 0.9375rem;
  font-weight: 600;
  color: hsl(var(--foreground));
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.j-section-title-subtitle-text {
  font-size: 0.8125rem;
  color: hsl(var(--muted-foreground));
  line-height: 1.4;
}
</style>
